// src/components/StudentNavbar.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import emailjs from '@emailjs/browser';
import {
  CalendarToday, InfoOutlined, Apps, Language, ExitToApp,
  ArrowLeft, ArrowRight, MailOutline, ArrowDropDown, SchoolOutlined, Close,
} from "@mui/icons-material";
import KeycloakContext from "../../keycloakContext";
import { useTranslation } from "react-i18next";
import { getFallbackVignette } from "../constants/vignetteCatalog";
import { getUserVignettes } from "../services/vignetteService";
import { logoutToLivingSoilsHome } from "../utils/keycloakNavigation";

// Component del Modal per mantenir el codi net
function ReportErrorModal({ isOpen, onClose, t, keycloak }) {
  const form = useRef();
  const fileInputRef = useRef(null); 

  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);

  const handleFileChange = (e) => {
    setAttachments(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const handleRemoveAttachment = (indexToRemove) => {
    const newAttachments = attachments.filter((_, index) => index !== indexToRemove);
    setAttachments(newAttachments);

    const dataTransfer = new DataTransfer();
    newAttachments.forEach(file => dataTransfer.items.add(file));
    fileInputRef.current.files = dataTransfer.files;
  };

  const sendEmail = (e) => {
    // 1. Atura el comportament per defecte del formulari, que recarregaria la pàgina.
    e.preventDefault();

    // 2. Lògica per gestionar si hi ha fitxers adjunts o no.
    //    Això evita que EmailJS doni error si no s'adjunta cap fitxer.
    if (attachments.length === 0) {
      if(fileInputRef.current) fileInputRef.current.name = "";
    } else {
      if(fileInputRef.current) fileInputRef.current.name = "attachment";
    }

    // 3. Activa l'estat de "enviant" per al botó i neteja missatges antics.
    setIsSending(true);
    setMessage("");

    // 4. Llegeix les claus de manera segura des de l'arxiu .env.
    //    Això només funcionarà si l'arxiu .env està ben col·locat i has reiniciat el servidor.
    const SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
    const PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

    // 5. Funció principal d'EmailJS que agafa les dades del formulari i les envia.
    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, form.current, PUBLIC_KEY)
      .then((result) => {
        // 6. Si l'enviament té èxit, mostra un missatge de confirmació.
        setMessage(t("studentNavbar.reportErrorModal.successMessage"));
        
        // Tanca la finestra automàticament després de 3 segons.
        setTimeout(() => {
          onClose();
          setMessage("");
          setAttachments([]); // Buidem la llista de fitxers.
        }, 3000);

      }, (error) => {
        // 7. Si hi ha un error, mostra un missatge d'error a l'usuari i a la consola.
        setMessage(t("studentNavbar.reportErrorModal.errorMessage"));
        console.error("EmailJS error:", error); 
      })
      .finally(() => {
        // 8. En qualsevol cas (èxit o error), desactiva l'estat de "enviant".
        setIsSending(false);
      });
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-stone-100 rounded-xl shadow-2xl p-6 w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
          <Close />
        </button>
        <h2 className="text-xl font-bold mb-6">{t("studentNavbar.reportErrorModal.title")}</h2>
        
        <form ref={form} onSubmit={sendEmail} className="flex flex-col gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("studentNavbar.reportErrorModal.emailLabel")}</label>
            <input 
              type="email" 
              name="reporter_email" 
              required 
              defaultValue={keycloak.tokenParsed?.email || ''}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500" 
            />
            {/* LÍNIA QUE FALTAVA: Camp ocult per enviar el nom de l'usuari */}
            <input type="hidden" name="reporter_name" defaultValue={keycloak.tokenParsed?.given_name || 'Usuari no registrat'} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("studentNavbar.reportErrorModal.descriptionLabel")}</label>
            <textarea name="problem_description" required rows="4" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"></textarea>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("studentNavbar.reportErrorModal.attachLabel")}</label>
            <input 
              type="file" 
              name="attachment"
              ref={fileInputRef}
              multiple 
              onChange={handleFileChange}
              id="file-upload-input"
              className="hidden"
            />
            <label htmlFor="file-upload-input" className="cursor-pointer inline-block mt-1 text-sm font-semibold text-green-700 bg-green-100 hover:bg-green-200 px-4 py-2 rounded-md">
              {t("studentNavbar.reportErrorModal.chooseFilesButton", "Tria els arxius")}
            </label>
          </div>

          {attachments.length > 0 && (
            <div className="mt-2 space-y-2">
              <p className="text-sm font-medium text-gray-700">Fitxers seleccionats:</p>
              <ul className="list-disc list-inside bg-white p-3 rounded-md border min-w-0">
                {attachments.map((file, index) => (
                  <li key={index} className="text-sm text-gray-800 flex min-w-0 justify-between items-center gap-2">
                    <span className="min-w-0 truncate">{file.name}</span>
                    <button type="button" onClick={() => handleRemoveAttachment(index)} className="text-red-500 hover:text-red-700">
                      <Close fontSize="small" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-3 mt-4 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-stone-200 rounded-md hover:bg-stone-300">
              {t("studentNavbar.reportErrorModal.cancelButton")}
            </button>
            <button type="submit" disabled={isSending} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300">
              {isSending ? t("studentNavbar.reportErrorModal.sendingButton") : t("studentNavbar.reportErrorModal.sendButton")}
            </button>
          </div>
          {message && <p className={`text-center mt-2 text-sm ${message.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>{message}</p>}
        </form>
      </div>
    </div>
  );
}

export default function StudentNavbar() {
  const keycloak = useContext(KeycloakContext);
  const { t, i18n } = useTranslation();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedVignette, setSelectedVignette] = useState(() => ({
    ...getFallbackVignette(),
    imagePath: localStorage.getItem("selectedVignettePath") || getFallbackVignette().imagePath,
  }));

  useEffect(() => {
    document.documentElement.style.setProperty("--app-navbar-height", "0px");
  }, []);


  useEffect(() => {
    const handleSelectedVignetteChanged = (event) => {
      if (event.detail?.imagePath) {
        setSelectedVignette(event.detail);
      }
    };

    window.addEventListener("selectedVignetteChanged", handleSelectedVignetteChanged);
    return () => window.removeEventListener("selectedVignetteChanged", handleSelectedVignetteChanged);
  }, []);

  useEffect(() => {
    const userId = keycloak.tokenParsed?.email || keycloak.tokenParsed?.preferred_username;
    if (!keycloak.authenticated || !userId) return;

    let cancelled = false;
    getUserVignettes(userId)
      .then((data) => {
        if (!cancelled && data?.selectedVignette) {
          setSelectedVignette(data.selectedVignette);
          localStorage.setItem("selectedVignettePath", data.selectedVignette.imagePath);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [keycloak.authenticated, keycloak.tokenParsed?.email, keycloak.tokenParsed?.preferred_username]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
  };

  const logout = () => {
    localStorage.removeItem("avatarUrl");
    localStorage.removeItem("avatarId");
    logoutToLivingSoilsHome(keycloak);
  };

  const avatarUrl =
    selectedVignette?.imagePath ||
    keycloak.tokenParsed?.picture ||
    localStorage.getItem("avatarUrl") ||
    "/images/default-avatar.png";
  
  const languageCode = (i18n.language || "ca").slice(0, 2).toUpperCase();
  const navWidthClass = collapsed ? "md:w-16 md:px-2" : "md:w-52 md:px-4";
  const textClass = "text-xs md:text-lg";

  return (
    <>
      <ReportErrorModal 
        isOpen={isReportModalOpen} 
        onClose={() => setIsReportModalOpen(false)} 
        t={t}
        keycloak={keycloak}
      />
      <nav className={`fixed bottom-3 left-3 right-3 top-auto z-40 flex flex-row items-center justify-around gap-2 rounded-xl bg-white/90 px-3 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.28)] ring-1 ring-black/10 backdrop-blur-md transition-all duration-300 md:inset-y-14 md:left-6 md:right-auto md:top-20 md:bottom-auto md:flex-col md:justify-between md:py-6 ${navWidthClass}`}>
        {/* Element superior */}
        <div className="hidden justify-between items-center md:flex">
          {!collapsed && (
            <div className="relative">
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center text-sm font-semibold text-gray-700 p-1.5 rounded-lg hover:bg-gray-200"
              >
                <Language fontSize="small" className="mr-1" />
                <span>{languageCode}</span>
                <ArrowDropDown fontSize="small" />
              </button>
              {langOpen && (
                <ul className="absolute left-0 top-full mt-2 w-24 bg-white shadow-lg rounded-md border border-gray-200 z-50">
                  {["ca", "es", "en"].map((lng) => (
                    <li
                      key={lng}
                      className="py-1.5 px-3 text-center hover:bg-green-100 cursor-pointer text-sm"
                      onClick={() => changeLanguage(lng)}
                    >
                      {lng.toUpperCase()}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="p-1 rounded-full hover:bg-gray-200"
          >
            {collapsed ? (
              <ArrowRight fontSize="small" />
            ) : (
              <ArrowLeft fontSize="small" />
            )}
          </button>
        </div>
      
        {/* Element central */}
        <ul className="flex flex-1 flex-row items-center justify-around gap-2 md:flex-none md:flex-col md:items-stretch md:justify-start md:gap-0 md:space-y-6">
          {[
            { to: "/game", icon: <Apps fontSize="small" />, label: t("studentNavbar.home", "Home") },
            { to: "/dashboard", icon: <CalendarToday fontSize="small" />, label: t("studentNavbar.calendar", t("classroom.calendar", "Calendar")) },
            { to: "/info", icon: <InfoOutlined fontSize="small" />, label: t("studentNavbar.info", "Info") },
            { to: "/account", icon: <SchoolOutlined fontSize="small" />, label: t("studentNavbar.profile", t("studentDashboard.myAccount", "Profile")), mobileOnly: true },
          ].map(({ to, icon, label, mobileOnly }) => (
            <li key={to} className={`min-w-0 ${mobileOnly ? "md:hidden" : "md:pl-1"}`}>
              <Link
                to={to}
                className={`flex min-w-0 flex-col items-center gap-1 rounded-xl px-2 py-1 text-center text-gray-800 hover:text-green-700 md:flex-row md:gap-3 md:px-0 md:py-0 md:text-left ${textClass}`}
              >
                {icon}
                <span className={`${collapsed ? "md:hidden" : ""} max-w-[5.5rem] truncate md:max-w-none`}>{label}</span>
              </Link>
            </li>
          ))}
        </ul>

        {/* Element inferior */}
        <div className="hidden flex-col gap-4 md:flex">
          {/* Secció d'usuari */}
          {keycloak.authenticated && (
            <div className="relative px-1">
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-3 w-full"
              >
                <img
                  src={avatarUrl}
                  alt="Avatar"
                  className="w-10 h-10 rounded-full object-contain"
                  onError={(e) => (e.currentTarget.src = "/images/default-avatar.png")}
                />
                {!collapsed && (
                  <span className={`${textClass} text-gray-800`}>
                    {keycloak.tokenParsed?.given_name || keycloak.tokenParsed?.preferred_username}
                  </span>
                )}
              </button>

              {userMenuOpen && !collapsed && (
                <ul className="absolute bottom-full mb-2 w-full bg-white shadow-md rounded-md border border-green-200 py-1 z-50">
                  <li className="px-3 py-2 hover:bg-green-50 text-sm">
                    <Link to="/account">{t("studentDashboard.myAccount", "El teu compte")}</Link>
                  </li>
                  <li onClick={() => { setIsReportModalOpen(true); setUserMenuOpen(false); }} className="px-3 py-2 hover:bg-green-50 text-sm flex items-center gap-2 cursor-pointer">
                    <MailOutline fontSize="small" />
                    <span>{t("studentNavbar.reportError")}</span>
                  </li>
                  <li className="px-3 py-2 hover:bg-green-50 text-red-600 flex items-center gap-2 cursor-pointer text-sm" onClick={logout}>
                    <ExitToApp fontSize="small" />
                    <span>{t("studentNavbar.logout", "Sortir")}</span>
                  </li>
                </ul>
              )}
            </div>
          )}
          
          
          
          {/* Secció de tutorial */}
          {!collapsed && (
            <div className="relative group px-2">
              <div className="text-gray-500 cursor-default text-center">
                {t("studentNavbar.tutorialTitle", "Tutorial")}
              </div>
              <div
                className="absolute hidden group-hover:block bottom-full mb-2 w-full p-3 
                           bg-gray-800 text-white rounded-lg shadow-xl ring-1 ring-black/5 
                           text-xs z-50 text-center transform -translate-x-2"
              >
                <ol className="list-decimal list-inside space-y-2 font-medium">
                  <li>{t("studentNavbar.tutorialStep1")}</li>
                  <li>{t("studentNavbar.tutorialStep2")}</li>
                  <li>{t("studentNavbar.tutorialStep3")}</li>
                  <li>{t("studentNavbar.tutorialStep4")}</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}